import { useEffect } from 'react';

import { identifyAnalyticsUser } from '@modernloop/shared/analytics';
import type { AnalyticsUser } from '@modernloop/shared/analytics';
import { useShouldLogStatistics } from '@modernloop/shared/hooks';

import type { UserInfo } from 'src/contexts/auth0/Auth0Context';

/**
 * Builds a vendor-neutral analytics user from hiring-dashboard auth info. The
 * Amplitude sink applies the required guards (user id + org) and property shape.
 */
function buildAnalyticsUser(userInfo: UserInfo): AnalyticsUser {
  return {
    kind: 'EMPLOYEE',
    userId: userInfo.userId,
    userEmail: userInfo.userEmail,
    employeeName: userInfo.employeeName,
    orgId: userInfo.orgId,
    orgName: userInfo.orgName,
  };
}

/**
 * Identifies the user across all registered analytics vendors (Amplitude, ...)
 * via the shared facade. Gated on analytics consent.
 *
 * Must be called within a child of AuthProvider and CookieConsentProvider.
 *
 * @param userInfo from useAuth();
 */
export const useIdentifyAnalyticsUser = (userInfo: UserInfo | null) => {
  const shouldLogStatistics = useShouldLogStatistics();
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes analytics APIs after render.
  useEffect(
    () => {
      if (!shouldLogStatistics || !userInfo) {
        return;
      }
      identifyAnalyticsUser(buildAnalyticsUser(userInfo));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(userInfo), shouldLogStatistics]
  );
};
